"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import {
  interpretQueueHttp,
  missingQueueProjection,
  type QueueErrorKind,
} from "@/lib/financialWork/queueClient";
import type { WorkQueueProjection } from "@/lib/financialWork/types";
import { useWorkspaceActivityAutoRefresh } from "@/hooks/useWorkspaceActivityAutoRefresh";
import { createMarketApiFetch } from "@/lib/marketApiFetch";
import { INCOME_MONEY_CHANGED_EVENT } from "@/lib/incomeMoneyRefresh";
import { getUserIdFromAuthToken } from "@/lib/getUserIdFromAuthToken";
import { subscribeAuthSession } from "@/lib/authSession";
import {
  subscribeReceivablePendingRefresh,
  type RefreshReceivablePendingDetail,
} from "@/lib/receivablePendingRefresh";

const marketApiFetch = createMarketApiFetch();

/**
 * Loads the BillLin work-queue projection for the current workspace.
 */
export function useFinancialWorkQueue(args: {
  workspaceId: string | null;
  ownerUserId?: string | null;
  enabled?: boolean;
  /** When this becomes true, refetch so งานเงิน matches ข้อมูล after match/undo. */
  refetchWhenVisible?: boolean;
  /** Shop outsider / no session — show ไม่มีสิทธิ์ instead of an empty queue. */
  accessDenied?: boolean;
}) {
  const {
    workspaceId,
    ownerUserId,
    enabled = true,
    refetchWhenVisible = false,
    accessDenied = false,
  } = args;
  const requestIdRef = useRef(0);
  const seenWorkspaceRef = useRef(workspaceId?.trim() ?? "");
  const [projection, setProjection] = useState<WorkQueueProjection | null>(
    null,
  );
  const [loading, setLoading] = useState(
    () => Boolean(enabled) && !accessDenied,
  );
  const [error, setError] = useState<string | null>(() =>
    accessDenied ? "ไม่มีสิทธิ์ดูคิวงานเงินนี้" : null,
  );
  const [errorKind, setErrorKind] = useState<QueueErrorKind | null>(() =>
    accessDenied ? "unauthorized" : null,
  );

  const workspaceKey = workspaceId?.trim() ?? "";
  if (seenWorkspaceRef.current !== workspaceKey) {
    seenWorkspaceRef.current = workspaceKey;
    setProjection(null);
    setError(
      accessDenied && workspaceKey ? "ไม่มีสิทธิ์ดูคิวงานเงินนี้" : null,
    );
    setErrorKind(
      accessDenied && workspaceKey ? "unauthorized" : null,
    );
    setLoading(Boolean(enabled && workspaceKey && !accessDenied));
  }

  const accessDeniedRef = useRef(accessDenied);
  if (accessDeniedRef.current !== accessDenied) {
    accessDeniedRef.current = accessDenied;
    if (accessDenied) {
      setProjection(null);
      setError("ไม่มีสิทธิ์ดูคิวงานเงินนี้");
      setErrorKind("unauthorized");
      setLoading(false);
    } else {
      setError(null);
      setErrorKind(null);
      setLoading(Boolean(enabled && workspaceKey));
    }
  }

  const enabledRef = useRef(enabled);
  if (enabledRef.current !== enabled) {
    enabledRef.current = enabled;
    if (enabled && !accessDenied) {
      setError(null);
      setErrorKind(null);
      setLoading(Boolean(workspaceKey));
    }
  }

  const reload = useCallback(async (opts?: { silent?: boolean }) => {
    if (!enabled) return;

    const requestId = ++requestIdRef.current;
    const silent = Boolean(opts?.silent);

    const ws = workspaceId?.trim() ?? "";
    const viewerId = getUserIdFromAuthToken()?.trim() || "";
    if (!ws) {
      setProjection(null);
      setError(null);
      setErrorKind(null);
      setLoading(false);
      return;
    }
    if (accessDenied) {
      setProjection(null);
      setError("ไม่มีสิทธิ์ดูคิวงานเงินนี้");
      setErrorKind("unauthorized");
      setLoading(false);
      return;
    }
    if (!viewerId) {
      // Token may land after first paint — keep preparing, never flash ไม่มีสิทธิ์.
      // Shell also withholds guest accessDenied until session is settled.
      if (!silent) {
        setProjection(null);
        setError(null);
        setErrorKind(null);
        setLoading(true);
      }
      return;
    }

    if (!silent) {
      setProjection(null);
      setLoading(true);
      setError(null);
      setErrorKind(null);
    }
    try {
      const qs = new URLSearchParams({ workspace_id: ws });
      const owner = ownerUserId?.trim() ?? "";
      qs.set("actor_id", viewerId);
      if (owner) {
        qs.set("owner_user_id", owner);
      }
      const res = await marketApiFetch(
        `/api/v1/financial-work/queue?${qs.toString()}`,
        { cache: "no-store", headers: { Accept: "application/json" } },
      );

      if (requestId !== requestIdRef.current) return;

      const body = await res.json().catch(() => ({}));
      const outcome = interpretQueueHttp(res.status, body);

      if (outcome.kind === "ok") {
        setProjection(outcome.projection);
        return;
      }
      if (outcome.kind === "missing") {
        setProjection(missingQueueProjection());
        return;
      }
      if (outcome.kind === "unauthorized") {
        setProjection(null);
        setError(outcome.message);
        setErrorKind("unauthorized");
        return;
      }
      throw new Error(outcome.message);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      const message = err instanceof Error ? err.message : "โหลดคิวไม่สำเร็จ";
      if (/failed to fetch|network|404|not found/i.test(message)) {
        setProjection(missingQueueProjection());
        setError(null);
        setErrorKind(null);
      } else if (!silent) {
        setError(message);
        setErrorKind("generic");
        setProjection(null);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [accessDenied, enabled, ownerUserId, workspaceId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (!enabled || accessDenied) return;
    if (getUserIdFromAuthToken()?.trim()) return;
    let timer = 0;
    const retry = () => {
      if (!getUserIdFromAuthToken()?.trim()) return;
      if (timer) window.clearInterval(timer);
      timer = 0;
      void reload();
    };
    const unsub = subscribeAuthSession(retry);
    timer = window.setInterval(retry, 150);
    return () => {
      unsub();
      if (timer) window.clearInterval(timer);
    };
  }, [accessDenied, enabled, reload]);

  useEffect(() => {
    if (!refetchWhenVisible) return;
    void reload({ silent: true });
  }, [refetchWhenVisible, reload]);

  // Same poll / focus / activity refresh as ยอดค้าง — LINE announcements
  // otherwise stay invisible on งานเงิน until a full page reload.
  useWorkspaceActivityAutoRefresh(enabled ? workspaceId : null, () =>
    reload({ silent: true }),
  );

  useEffect(() => {
    let cancelled = false;
    const timers: number[] = [];
    const clearTimers = () => {
      for (const id of timers) window.clearTimeout(id);
      timers.length = 0;
    };
    const bump = (detail?: RefreshReceivablePendingDetail) => {
      clearTimers();
      void reload({ silent: true });
      const retries = Math.max(0, detail?.retries ?? 2);
      const delayMs = Math.max(50, detail?.retryDelayMs ?? 220);
      for (let i = 0; i < retries; i += 1) {
        timers.push(
          window.setTimeout(() => {
            if (!cancelled) void reload({ silent: true });
          }, delayMs * (i + 1)),
        );
      }
    };
    const onIncome = () => {
      bump({ retries: 2, retryDelayMs: 220, reason: "income_money_changed" });
    };
    window.addEventListener(INCOME_MONEY_CHANGED_EVENT, onIncome);
    const unsubPending = subscribeReceivablePendingRefresh((detail) => bump(detail));
    return () => {
      cancelled = true;
      clearTimers();
      window.removeEventListener(INCOME_MONEY_CHANGED_EVENT, onIncome);
      unsubPending();
    };
  }, [reload]);

  return { projection, loading, error, errorKind, reload };
}
